import { Box, Divider, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import {
  checkAssetsValidEn,
  checkAssetsValidTh,
} from '@Containers/UploadImageFormFields/utils';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import {
  validateEnglishDescription,
  validateThaiDescription,
} from '@Utils/validation';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { Role } from 'src/Enum/Report';
import {
  checkBoardResponsibilityUrlValid,
  checkClimateChangeBoardResponsibilityEnValid,
  checkClimateChangeComplete,
  checkClimateChangeCompleteEnValid,
  checkClimateChangeSubcommitteeResponsibilityEnValid,
  checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid,
  checkClimateChangeTermEnValid,
  checkClimateChangeValidateSpace,
  checkClimateChangeValidateSpaceIntro,
  checkClimateChangeValidateSpaceRemark,
  checkHasClimateChangeValid,
} from '../ClimateChangeStructure/utils';
import { useGreenhouseForm } from '../EsgGreenhouseFormProvider';
import {
  IClimateChangeStructureFormValues,
  IEsgGreenhousePrincipleFormValues,
  IPerformanceGhGasMgmtFormValues,
} from '../EsgGreenhouseFormProvider/types';
import {
  checkGhGasEmissionVerifyEn,
  checkGhGasEmissionVerifyRemarkSpaceValid,
  checkGhGasEmissionVerifySpaceValid,
  checkGhGasEmissionVerifyTh,
  checkGhGasEmissionVolumeRemarkEnValid,
  checkGhGasEmissionVolumeRemarkSpaceValid,
  checkPerfGhGasMgmtIntroSpaceValid,
  checkPerfGhGasMgmtRemarkEnValid,
  checkPerfGhGasMgmtReportIntroRemarkEnValid,
  checkPerfGhGasMgmtReportIntroSpaceValid,
  checkPerfGhGasMgmtReportRemarkSpaceValid,
  checkPerformanceGhGasMgmtEn,
  checkPerformanceGhGasMgmtTh,
} from '../GreenhouseGasMgmtReport/utils';
import {
  checkCarbonEnValid,
  checkCarbonThValid,
  checkGhGoalEnValid,
  checkGhGoalTermComplete,
  checkGhGoalTermEnValid,
  checkGhOtherEnValid,
  checkGhOtherThValid,
  checkIntroRemarkValid,
  checkNetZeroEnValid,
  checkNetZeroThValid,
} from '../GreenhouseGoalTerm/utils';
import {
  checkPrincipleCompletionEn,
  checkPrincipleCompletionTh,
} from '../GreenhouseStandard/utils';
import {
  checkGhStrategyCompletionEn,
  checkGhStrategyCompletionTh,
  checkGhStrategyIntroductionValidEn,
  checkGhStrategyIntroductionValidTh,
  checkGhStrategyPlanRemarkValidEn,
  checkGhStrategyPlanRemarkValidTh,
  checkGhStrategyRemarkValidEn,
  checkGhStrategyRemarkValidTh,
  checkGhStrategyUrlValid,
} from '../GreenhouseStrategy/utils';
import { checkValidRemarkEn } from '@Utils/validationIntroRemark';

const checkAllComplete = (
  ghStandardCompleted?: boolean,
  ghGoalTermCompleted?: boolean,
  ghStrategyCompleted?: boolean,
  performanceGhgasMgmtReportCompleted?: boolean,
  climateChangeStructureCompleted?: boolean
) => {
  switch (true) {
    case ghStandardCompleted &&
      ghGoalTermCompleted &&
      ghStrategyCompleted &&
      performanceGhgasMgmtReportCompleted &&
      climateChangeStructureCompleted:
      return true;
    default:
      return false;
  }
};

const EsgGreenhouseCheckAll = () => {
  const { setValue, control, watch } = useGreenhouseForm();
  const { isPublished } = useProgressBar();
  const [
    greenhouseStandard,
    greenhouseGoalTerm,
    greenhouseStrategy,
    performanceGhGasMgmtReport,
    otherDescriptions,
  ] = useWatch({
    name: [
      'greenhouseStandard',
      'greenhouseGoalTerm',
      'greenhouseStrategy',
      'performanceGhGasMgmtReport',
      'otherDescriptions',
    ],
    control,
  });

  const climateChangeStructure = watch(
    'climateChangeStructure'
  ) as IClimateChangeStructureFormValues;

  const principles: Record<string, IEsgGreenhousePrincipleFormValues> =
    greenhouseStandard?.principles ?? {};
  const { greenhouseGoal, netZero, carbon, other } = greenhouseGoalTerm || {};
  const { performanceGhGasMgmt, ghGasEmissionVerify, ghGasEmissionVolume } =
    performanceGhGasMgmtReport || {};
  const { boardResponsibility, subcommitteeResponsibility } =
    climateChangeStructure || {};

  const ghStandardCompleted =
    greenhouseStandard?.completedSection as CompleteSectionFormValues;
  const ghGoalTermCompleted =
    greenhouseGoalTerm?.completedSection as CompleteSectionFormValues;
  const ghStrategyCompleted =
    greenhouseStrategy?.completedSection as CompleteSectionFormValues;
  const performanceGhgasMgmtReportCompleted =
    performanceGhGasMgmtReport?.completedSection as CompleteSectionFormValues;
  const climateChangeStructureCompleted =
    climateChangeStructure?.completedSection as CompleteSectionFormValues;

  useEffect(() => {
    const determineCompletion = (language: 'Th' | 'En') => {
      const isLanguageCompleted = checkAllComplete(
        ghStandardCompleted?.[`isCompleted${language}`],
        ghGoalTermCompleted?.[`isCompleted${language}`],
        ghStrategyCompleted?.[`isCompleted${language}`],
        performanceGhgasMgmtReportCompleted?.[`isCompleted${language}`],
        climateChangeStructureCompleted?.[`isCompleted${language}`]
      );

      if (!otherDescriptions?.length) {
        return isLanguageCompleted;
      }

      const validator =
        language === 'Th'
          ? validateThaiDescription
          : validateEnglishDescription;

      return isLanguageCompleted && validator(otherDescriptions);
    };

    setValue('completedSection.isCompletedTh', determineCompletion('Th'));
    setValue('completedSection.isCompletedEn', determineCompletion('En'));
  }, [
    ghStandardCompleted,
    ghGoalTermCompleted,
    ghStrategyCompleted,
    performanceGhgasMgmtReportCompleted,
    climateChangeStructureCompleted,
    setValue,
    otherDescriptions,
  ]);

  const isGhStandardCompleteTh = checkPrincipleCompletionTh(
    principles,
    greenhouseStandard?.remark
  );
  const isGhStandardCompleteEn = checkPrincipleCompletionEn(
    principles,
    greenhouseStandard?.remark
  );

  const isGhGoalTermCompleteTh =
    greenhouseGoalTerm &&
    checkGhGoalTermComplete(greenhouseGoalTerm) &&
    checkIntroRemarkValid(greenhouseGoalTerm) &&
    checkNetZeroThValid(netZero) &&
    checkCarbonThValid(carbon) &&
    checkGhOtherThValid(other);

  const isGhGoalTermCompleteEn =
    greenhouseGoalTerm &&
    checkGhGoalTermComplete(greenhouseGoalTerm) &&
    checkGhGoalTermEnValid(greenhouseGoalTerm) &&
    checkGhGoalEnValid(greenhouseGoal) &&
    checkNetZeroEnValid(netZero) &&
    checkCarbonEnValid(carbon) &&
    checkGhOtherEnValid(other);

  const isGhStrategyCompleteTh =
    checkGhStrategyCompletionTh(greenhouseStrategy) &&
    checkGhStrategyIntroductionValidTh(greenhouseStrategy) &&
    checkGhStrategyRemarkValidTh(greenhouseStrategy) &&
    checkGhStrategyPlanRemarkValidTh(greenhouseStrategy) &&
    checkGhStrategyUrlValid(greenhouseStrategy, 'Th');

  const isGhStrategyCompleteEn =
    checkGhStrategyCompletionEn(greenhouseStrategy) &&
    checkGhStrategyIntroductionValidEn(greenhouseStrategy) &&
    checkGhStrategyRemarkValidEn(greenhouseStrategy) &&
    checkGhStrategyPlanRemarkValidEn(greenhouseStrategy) &&
    checkGhStrategyUrlValid(greenhouseStrategy, 'En');

  const isPerformanceGhgasMgmtReportCompleteTh =
    checkPerformanceGhGasMgmtTh(
      performanceGhGasMgmt as IPerformanceGhGasMgmtFormValues
    ) &&
    ghGasEmissionVerify !== undefined &&
    checkGhGasEmissionVerifyTh(ghGasEmissionVerify) &&
    checkAssetsValidTh(performanceGhGasMgmt) &&
    performanceGhGasMgmtReport &&
    checkPerfGhGasMgmtReportIntroSpaceValid(performanceGhGasMgmtReport, 'Th') &&
    checkPerfGhGasMgmtReportRemarkSpaceValid(
      performanceGhGasMgmtReport,
      'Th'
    ) &&
    performanceGhGasMgmt &&
    checkPerfGhGasMgmtIntroSpaceValid(performanceGhGasMgmt, 'Th') &&
    ghGasEmissionVolume !== undefined &&
    checkGhGasEmissionVolumeRemarkSpaceValid(ghGasEmissionVolume, 'Th') &&
    checkGhGasEmissionVerifySpaceValid(ghGasEmissionVerify, 'Th') &&
    checkGhGasEmissionVerifyRemarkSpaceValid(ghGasEmissionVerify, 'Th');

  const isPerformanceGhgasMgmtReportCompleteEn =
    checkPerformanceGhGasMgmtEn(
      performanceGhGasMgmt as IPerformanceGhGasMgmtFormValues
    ) &&
    ghGasEmissionVerify !== undefined &&
    ghGasEmissionVolume !== undefined &&
    performanceGhGasMgmtReport &&
    performanceGhGasMgmt &&
    checkPerfGhGasMgmtReportIntroRemarkEnValid(performanceGhGasMgmtReport) &&
    checkPerfGhGasMgmtRemarkEnValid(performanceGhGasMgmt) &&
    checkGhGasEmissionVerifyEn(ghGasEmissionVerify) &&
    checkAssetsValidEn(performanceGhGasMgmt) &&
    checkGhGasEmissionVolumeRemarkEnValid(ghGasEmissionVolume) &&
    checkPerfGhGasMgmtReportIntroSpaceValid(performanceGhGasMgmtReport, 'En') &&
    checkPerfGhGasMgmtReportRemarkSpaceValid(
      performanceGhGasMgmtReport,
      'En'
    ) &&
    checkPerfGhGasMgmtIntroSpaceValid(performanceGhGasMgmt, 'En') &&
    checkGhGasEmissionVolumeRemarkSpaceValid(ghGasEmissionVolume, 'En') &&
    checkGhGasEmissionVerifySpaceValid(ghGasEmissionVerify, 'En') &&
    checkGhGasEmissionVerifyRemarkSpaceValid(ghGasEmissionVerify, 'En') &&
    checkValidRemarkEn(ghGasEmissionVerify?.remark);

  const isClimateChangeStructureCompleteTh =
    checkClimateChangeComplete(climateChangeStructure, 'Th') &&
    checkClimateChangeValidateSpace(climateChangeStructure, 'Th') &&
    checkBoardResponsibilityUrlValid(boardResponsibility, 'Th') &&
    checkHasClimateChangeValid(climateChangeStructure) &&
    checkClimateChangeValidateSpaceIntro(climateChangeStructure, 'Th') &&
    checkClimateChangeValidateSpaceRemark(climateChangeStructure, 'Th') &&
    checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid(
      subcommitteeResponsibility,
      'Th'
    );

  const isClimateChangeStructureCompleteEn =
    checkClimateChangeComplete(climateChangeStructure, 'En') &&
    checkClimateChangeCompleteEnValid(climateChangeStructure) &&
    checkClimateChangeTermEnValid(climateChangeStructure) &&
    checkClimateChangeBoardResponsibilityEnValid(boardResponsibility) &&
    checkClimateChangeSubcommitteeResponsibilityEnValid(
      subcommitteeResponsibility
    ) &&
    checkBoardResponsibilityUrlValid(boardResponsibility, 'En') &&
    checkHasClimateChangeValid(climateChangeStructure) &&
    checkClimateChangeValidateSpace(climateChangeStructure, 'En') &&
    checkClimateChangeValidateSpaceIntro(climateChangeStructure, 'En') &&
    checkClimateChangeValidateSpaceRemark(climateChangeStructure, 'En') &&
    checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid(
      subcommitteeResponsibility,
      'En'
    );

  const isOtherDescriptionValidTh = validateThaiDescription(
    otherDescriptions ?? []
  );
  const isOtherDescriptionValidEn = validateEnglishDescription(
    otherDescriptions ?? []
  );

  const handleEachThComplete = (value: boolean) => {
    setValue('greenhouseStandard.completedSection.isCompletedTh', value);
    setValue('greenhouseGoalTerm.completedSection.isCompletedTh', value);
    setValue('greenhouseStrategy.completedSection.isCompletedTh', value);
    setValue(
      'performanceGhGasMgmtReport.completedSection.isCompletedTh',
      value
    );
    setValue('climateChangeStructure.completedSection.isCompletedTh', value);
  };

  const handleEachEnComplete = (value: boolean) => {
    setValue('greenhouseStandard.completedSection.isCompletedEn', value);
    setValue('greenhouseGoalTerm.completedSection.isCompletedEn', value);
    setValue('greenhouseStrategy.completedSection.isCompletedEn', value);
    setValue(
      'performanceGhGasMgmtReport.completedSection.isCompletedEn',
      value
    );
    setValue('climateChangeStructure.completedSection.isCompletedEn', value);
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    if (language === 'Th') {
      setValue('greenhouseStandard.completedSection.dateTh', undefined);
      setValue('greenhouseStandard.completedSection.roleStampTh', Role.NONE);

      setValue('greenhouseGoalTerm.completedSection.dateTh', undefined);
      setValue('greenhouseGoalTerm.completedSection.roleStampTh', Role.NONE);

      setValue('greenhouseStrategy.completedSection.dateTh', undefined);
      setValue('greenhouseStrategy.completedSection.roleStampTh', Role.NONE);

      setValue('performanceGhGasMgmtReport.completedSection.dateTh', undefined);
      setValue(
        'performanceGhGasMgmtReport.completedSection.roleStampTh',
        Role.NONE
      );
      setValue('climateChangeStructure.completedSection.dateTh', undefined);
      setValue(
        'climateChangeStructure.completedSection.roleStampTh',
        Role.NONE
      );
    } else if (language === 'En') {
      setValue('greenhouseStandard.completedSection.dateEn', undefined);
      setValue('greenhouseStandard.completedSection.roleStampEn', Role.NONE);

      setValue('greenhouseGoalTerm.completedSection.dateEn', undefined);
      setValue('greenhouseGoalTerm.completedSection.roleStampEn', Role.NONE);

      setValue('greenhouseStrategy.completedSection.dateEn', undefined);
      setValue('greenhouseStrategy.completedSection.roleStampEn', Role.NONE);

      setValue('performanceGhGasMgmtReport.completedSection.dateEn', undefined);
      setValue(
        'performanceGhGasMgmtReport.completedSection.roleStampEn',
        Role.NONE
      );
      setValue('climateChangeStructure.completedSection.dateEn', undefined);
      setValue(
        'climateChangeStructure.completedSection.roleStampEn',
        Role.NONE
      );
    }
  };

  return (
    <Box bgcolor={'white'}>
      <Box p={2}>
        <Typography fontWeight={800}>
          กรณีต้องการยืนยันความถูกต้องทุกหัวข้อ
        </Typography>
      </Box>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight={800} textAlign={'right'}>
            ส่วนการยืนยันการตรวจสอบข้อมูลครบถ้วนทุกหัวข้อ
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{
                disabled:
                  isPublished ||
                  !isGhStandardCompleteTh ||
                  !isGhGoalTermCompleteTh ||
                  !isGhStrategyCompleteTh ||
                  !isPerformanceGhgasMgmtReportCompleteTh ||
                  !isClimateChangeStructureCompleteTh ||
                  !isOtherDescriptionValidTh,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.checked;
                handleEachThComplete(value);
                handleCheckboxChange(event, 'Th');
              }}
            />
          </Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{
                disabled:
                  isPublished ||
                  !isGhStandardCompleteEn ||
                  !isGhGoalTermCompleteEn ||
                  !isGhStrategyCompleteEn ||
                  !isPerformanceGhgasMgmtReportCompleteEn ||
                  !isClimateChangeStructureCompleteEn ||
                  !isOtherDescriptionValidEn,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.checked;
                handleEachEnComplete(value);
                handleCheckboxChange(event, 'En');
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default EsgGreenhouseCheckAll;
